import { useDebounceFn } from '@vueuse/core';
import { type Ref, toRef, toValue, watch } from 'vue';
import { type LocationQueryRaw, type Router, useRoute, useRouter } from 'vue-router';
import { getIsoDateFormat } from '@/core/utils/date';
import { deepCloneObject } from '@/core/utils/object';
import { isBoolean } from '@/core/utils/type-guards';

let queries = {} as LocationQueryRaw;
let $router: Router;

const debounce = useDebounceFn(() => {
  $router.replace({
    query: queries,
  });
}, 200);

export const useQueryValue = (
  value: Ref<unknown>,
  queryName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  serializer: UrlToStateSerializer<any>,
) => {
  $router = useRouter();
  const $route = useRoute();

  watch(
    () => $route.query,
    () => {
      queries = deepCloneObject($route.query);
    },
  );

  watch(
    value,
    () => {
      const queryValue = serializer.serialize(toValue(value)) || undefined;

      if (queryValue) {
        queries[queryName] = queryValue;
      } else {
        delete queries[queryName];
      }

      debounce();
    },
    {
      deep: true,
    },
  );

  const queryString = toRef(() => $route.query[queryName]);

  watch(
    queryString,
    () => {
      if (queryString.value) {
        value.value = serializer.deserialize(String(queryString.value));
      }
    },
    {
      immediate: true,
      deep: true,
    },
  );
};

export interface UrlToStateSerializer<T> {
  deserialize(queryValue: string | null | undefined): T;

  serialize(value: T): string | null | undefined;
}

export const dateArrayUrlSerializer: UrlToStateSerializer<Date[]> = {
  deserialize: (v) => (!v ? [] : v.split(',').map((x) => new Date(x))),
  serialize: (v) => (v.length === 0 ? undefined : v.map((x) => getIsoDateFormat(x)).join(',')),
};

export const stringUrlSerializer: UrlToStateSerializer<string> = {
  deserialize: (v) => (v === undefined ? '' : String(v)),
  serialize: (v) => (v === '' ? undefined : v),
};

export const numberUrlSerializer: UrlToStateSerializer<number> = {
  deserialize: (v) => (v ? +v : 0),
  serialize: (v) => (!v || v === 0 ? undefined : (+v).toString()),
};

export const booleanUrlSerializer: UrlToStateSerializer<boolean> = {
  deserialize: (v) => v === '1',
  serialize: (v) => (v ? '1' : undefined),
};

export const booleanStrictUrlSerializer: UrlToStateSerializer<boolean | undefined> = {
  deserialize: (v) => (v ? v === '1' : undefined),
  serialize: (v) => (isBoolean(v) ? (v ? '1' : '0') : undefined),
};
